import * as React from "react";

import { useMediaQuery, useTheme } from "@mui/material";

import { ExpressFormTemplateDesktop } from "./ExpressFormTemplateDesktop";
import { ExpressFormTemplateMobile } from "./ExpressFormTemplateMobile";
import { identity } from "lodash";
import { Help } from "@/components/atoms/HelpInfo";
import { FormMenuEntry } from "@/types/index";

export type ExpressFormTemplateProps = {
  noSave?: boolean;
  menuEntries: FormMenuEntry[];
  inPartnership: boolean;
  logoURL?: string;
  companyName: string;
  busy?: boolean;
  currentSection?: string;
  error?: string;
  nextLabel?: string;
  saveLabel?: string | null;
  children: React.ReactNode;
  onNext?: () => void;
  onSave?: () => void;
  onSectionClick: (section: string) => void;
  nestedFunc?: boolean;
  onAdd?: () => void;
  onBack?: () => void;
  addBtnLabel?: string;
  backBtnLabel?: string;
};

export const ExpressFormTemplate: React.FC<ExpressFormTemplateProps> = ({
  menuEntries,
  inPartnership,
  logoURL,
  companyName,
  busy,
  currentSection,
  error,
  children,
  nextLabel,
  onNext,
  onSectionClick,
  nestedFunc,
  onAdd,
  onBack,
  addBtnLabel,
  backBtnLabel,
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  if (matches) {
    return (
      <ExpressFormTemplateMobile
        menuEntries={menuEntries}
        busy={busy}
        currentSection={currentSection}
        error={error}
        nextLabel={nextLabel}
        onNext={onNext}
        onSectionClick={onSectionClick}
        nestedFunc={nestedFunc}
        onAdd={onAdd}
        onBack={onBack}
        addBtnLabel={addBtnLabel}
        backBtnLabel={backBtnLabel}
      >
        {children}
        <br></br>
        <Help />
      </ExpressFormTemplateMobile>
    );
  }

  return (
    <ExpressFormTemplateDesktop
      menuEntries={menuEntries}
      inPartnership={inPartnership}
      logoURL={logoURL}
      companyName={companyName}
      busy={busy}
      currentSection={currentSection}
      error={error}
      nextLabel={nextLabel}
      onNext={onNext}
      onSectionClick={onSectionClick}
      nestedFunc={nestedFunc}
      onAdd={onAdd}
      onBack={onBack}
      addBtnLabel={addBtnLabel}
      backBtnLabel={backBtnLabel}
    >
      {children}
    </ExpressFormTemplateDesktop>
  );
};

// ExpressTemplateState
export type ExpressTemplateState = {
  busy?: boolean;
  currentSection?: string;
  nextLabel?: string;
  saveLabel?: string | null;
  onNext?: () => void;
  onSave?: () => void;
  onSectionClick?: (section: string) => void;
  nestedFunc?: boolean;
  onAdd?: () => void;
  onBack?: () => void;
  addBtnLabel?: string;
  backBtnLabel?: string;
};

const useExpressFormTemplate = () => {
  const [
    {
      busy,
      currentSection,
      nextLabel,
      saveLabel,
      onNext,
      onSave,
      onSectionClick,
      nestedFunc,
      onAdd,
      onBack,
      addBtnLabel,
      backBtnLabel,
    },
    update,
  ] = React.useState<ExpressTemplateState>({
    onSectionClick: identity,
  });

  return {
    busy,
    currentSection,
    nextLabel,
    saveLabel,
    onNext,
    onSave,
    onSectionClick,
    update,
    nestedFunc,
    onAdd,
    onBack,
    addBtnLabel,
    backBtnLabel,
  };
};

export type UpdateExpressFormTemplateProc = (state: ExpressTemplateState) => void;

type WithExpressFormTemplateProps = {
  inPartnership: boolean;
  noSave?: boolean;
  logoURL?: string;
  companyName: string;
  menuEntries: FormMenuEntry[];
  proc: (update: UpdateExpressFormTemplateProc) => React.ReactNode;
};

export const WithExpressFormTemplate: React.FC<WithExpressFormTemplateProps> = ({
  inPartnership,
  logoURL,
  companyName,
  menuEntries,
  proc,
}) => {
  const {
    busy,
    currentSection,
    nextLabel,
    saveLabel,
    onNext,
    onSave,
    onSectionClick,
    update,
    nestedFunc,
    onAdd,
    onBack,
    addBtnLabel,
    backBtnLabel,
  } = useExpressFormTemplate();

  return (
    <ExpressFormTemplate
      menuEntries={menuEntries}
      busy={busy}
      inPartnership={inPartnership}
      logoURL={logoURL}
      companyName={companyName}
      currentSection={currentSection}
      nextLabel={nextLabel}
      saveLabel={saveLabel}
      onNext={onNext}
      onSave={onSave}
      onSectionClick={onSectionClick || identity}
      nestedFunc={nestedFunc}
      onAdd={onAdd}
      onBack={onBack}
      addBtnLabel={addBtnLabel}
      backBtnLabel={backBtnLabel}
    >
      {proc(update)}
    </ExpressFormTemplate>
  );
};
